import type { ExtrapolationResult } from '../../services/energyExtrapolationService';
import { ELEC_COM_FORMAT, ELEC_RES_FORMAT, type EnergyFormatTable } from '../../services/energyConsumptionSources';
import { buildEia861CanonicalElectricitySeries } from './buildEia861CanonicalSeries';
import type { Eia861ActivityStatus, Eia861PilotUtilityConfig } from './types';

function mapStatusToExtrapolationSource(
  status: Eia861ActivityStatus
): 'observed' | 'interpolated' | 'extrapolated' {
  if (status === 'observed_eia861') return 'observed';
  if (status === 'interpolated_between_eia_observations') return 'interpolated';
  return 'extrapolated';
}

function providerLabel(config: Eia861PilotUtilityConfig, utilityName: string, status: Eia861ActivityStatus): string {
  const base = `EIA-861 utility ${config.eiaUtilityId} (${utilityName})`;
  if (status === 'observed_eia861') return base;
  return `${base}; ${status}`;
}

export function applyEia861PilotToExtrapolationResult(
  config: Eia861PilotUtilityConfig,
  table: EnergyFormatTable,
  years: number[],
  legacy: ExtrapolationResult
): ExtrapolationResult {
  if (table !== ELEC_RES_FORMAT && table !== ELEC_COM_FORMAT) return legacy;
  const sector = table === ELEC_RES_FORMAT ? 'residential' : 'commercial';
  const canonical = buildEia861CanonicalElectricitySeries(config, years);
  const values = canonical.map((row) =>
    sector === 'residential' ? row.residential.kwh : row.commercial.kwh
  );
  const sources = canonical.map((row) =>
    mapStatusToExtrapolationSource(
      sector === 'residential' ? row.residential.status : row.commercial.status
    )
  );
  const providers = canonical.map((row) => {
    const s = sector === 'residential' ? row.residential : row.commercial;
    return providerLabel(config, s.utilityName, s.status);
  });
  const dataSources = canonical.map((row) => {
    const s = sector === 'residential' ? row.residential : row.commercial;
    if (s.status !== 'observed_eia861') return null;
    return `EIA-861 Sales to Ultimate Customers; ${s.sourceFile ?? 'unknown file'}`;
  });
  return {
    years,
    values,
    sources,
    providers,
    dataSources,
  };
}
